import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { INotificationBar } from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { registerStore } from "store/utils/reset-store.utils";

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

type SupplierNotificationBarState = {
  data: Record<string, INotificationBar[]>;
  pagination: Record<string, PaginationMeta>;
  isLoading: boolean;
};

type FetchParams = {
  supplierCompanyId: string;
  page: number;
  limit: number;
  replace?: boolean;
};

type LoadMoreParams = {
  supplierCompanyId: string;
  limit: number;
};

type SupplierNotificationBarActions = {
  fetch: (req: FetchParams) => Promise<void>;
  reload: (supplierCompanyId: string) => void;
  loadMore: (data: LoadMoreParams) => void;
};

export const useSupplierNotificationBar = create<
  SupplierNotificationBarState & SupplierNotificationBarActions
>()(
  devtools((set, get) => ({
    data: {},
    pagination: {},
    isLoading: false,

    fetch: async ({ supplierCompanyId, page, limit, replace = false }) => {
      const { data, isLoading } = get();

      const alreadyFetched =
        !replace && page === 1 && data[supplierCompanyId]?.length;
      if (alreadyFetched || isLoading) return;

      set({ isLoading: true });

      try {
        const res = await apiV2.notificationBar.getMany({
          supplierCompanyId,
          page,
          limit,
        });

        set((state) => {
          const prevItems = replace ? [] : state.data[supplierCompanyId] || [];

          return {
            data: {
              ...state.data,
              [supplierCompanyId]: [...prevItems, ...res.items],
            },
            pagination: {
              ...state.pagination,
              [supplierCompanyId]: {
                page: res.meta.currentPage,
                limit: res.meta.itemsPerPage,
                total: res.meta.totalItems ?? 0,
              },
            },
          };
        });
      } catch (err) {
        console.error("Error fetching supplier notifications:", err);
      } finally {
        set({ isLoading: false });
      }
    },

    // The current items stay until the first page is back: emptying the list
    // first showed as the notifications vanishing and reappearing.
    reload: async (supplierCompanyId) => {
      const { fetch } = get();

      await fetch({ supplierCompanyId, page: 1, limit: 15, replace: true });
    },

    loadMore: ({ supplierCompanyId, limit }) => {
      const { pagination, data, isLoading, fetch } = get();
      const currentPage = pagination[supplierCompanyId]?.page || 0;
      const total = pagination[supplierCompanyId]?.total || 0;
      const currentLength = data[supplierCompanyId]?.length || 0;

      if (isLoading || currentLength >= total) return;

      const nextPage = currentPage + 1;
      fetch({ supplierCompanyId, page: nextPage, limit });
    },
  }))
);

registerStore(useSupplierNotificationBar);
